import type { ReactNode } from "react";

type Tilt = "left" | "center" | "right";
type Size = "sm" | "md" | "lg";

interface PhoneFrameProps {
	children?: ReactNode;
	tilt?: Tilt;
	size?: Size;
	className?: string;
	label?: string;
}

const TILT_CLASS: Record<Tilt, string> = {
	left: "-rotate-6",
	center: "rotate-0",
	right: "rotate-6",
};

interface SizeSpec {
	frame: string;
	screen: string;
	island: string;
	bezel: number;
}

const SIZE_SPEC: Record<Size, SizeSpec> = {
	sm: { frame: "h-[560px] w-[280px] rounded-[2.6rem] border-[6px]", screen: "rounded-[2.2rem]", island: "h-5 w-[76px]", bezel: 6 },
	md: { frame: "h-[640px] w-[320px] rounded-[3rem] border-[8px]", screen: "rounded-[2.5rem]", island: "h-[22px] w-[88px]", bezel: 8 },
	lg: { frame: "h-[720px] w-[360px] rounded-[3.4rem] border-[10px]", screen: "rounded-[2.8rem]", island: "h-6 w-[100px]", bezel: 10 },
};

export function PhoneFrame({ children, tilt = "center", size = "md", className = "", label }: PhoneFrameProps) {
	const spec = SIZE_SPEC[size];
	return (
		<div
			role="img"
			aria-label={label ?? "Mobile app preview"}
			className={`relative ${TILT_CLASS[tilt]} ${className}`}
			data-mock="phone-frame"
		>
			<span aria-hidden="true" className="absolute -left-[3px] top-24 h-6 w-[3px] rounded-l-sm bg-neutral-800" />
			<span aria-hidden="true" className="absolute -left-[3px] top-36 h-11 w-[3px] rounded-l-sm bg-neutral-800" />
			<span aria-hidden="true" className="absolute -left-[3px] top-[200px] h-11 w-[3px] rounded-l-sm bg-neutral-800" />
			<span aria-hidden="true" className="absolute -right-[3px] top-40 h-16 w-[3px] rounded-r-sm bg-neutral-800" />
			<div className={`relative overflow-hidden border-neutral-950 bg-neutral-950 shadow-2xl shadow-brand-900/30 ${spec.frame}`}>
				<div className={`absolute inset-0 overflow-hidden bg-white ${spec.screen}`}>{children}</div>
				<div
					aria-hidden="true"
					className={`pointer-events-none absolute left-1/2 top-2.5 z-30 -translate-x-1/2 rounded-full bg-black ${spec.island}`}
					style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)" }}
				/>
				<div
					aria-hidden="true"
					className={`pointer-events-none absolute inset-0 z-20 ${spec.screen}`}
					style={{ boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 0 ${spec.bezel * 2}px rgba(0,0,0,0.18)` }}
				/>
			</div>
		</div>
	);
}
